import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { LIFE_QUOTE_STEP_KEYS } from "@/D2C/features/life-quote/common/static";
import { saveLifeQuoteStepFor } from "@/D2C/features/life-quote/redux/lifeQuoteSlice";
import {
  createLifeSession,
  updateLifeSessionStep,
} from "@/D2C/features/life-quote/services/service";
import {
  buildLifeCreateSessionPayload,
  buildLifeQuoteRequestBody,
} from "@/D2C/features/life-quote/utils/lifeQuotePayloadsUtils";
import { App_Url_New, LOB_IDS } from "@/shared/common/static";
import { SESSION_STEP_KEYS } from "@/shared/constants/sessionStepKeys";
import { setSession } from "@/shared/redux/reducers/authSlice";
import { clearQuoteList, setQuoteList, sortQuotes } from "@/shared/redux/reducers/commonSlice";
import { setSharedPersonalDetails } from "@/shared/redux/reducers/personalDetailsSlice";
import type { AppDispatch } from "@/shared/redux/store/store";
import { streamQuotes } from "@/shared/services/quoteStreamService";
import { getSessionIds, type SessionIds } from "../utils/getSessionIds";
import type { useQuoteRefs } from "./useQuoteRefs";

type Refs = ReturnType<typeof useQuoteRefs>;

export const useLifeQuotes = (
  refs: Pick<Refs, "lifePersonalRef" | "lifePlanDetailsRef">,
  setActiveSessionIds: (ids: SessionIds) => void
) => {
  const dispatch = useDispatch<AppDispatch>();

  return useCallback(async () => {
    const personalValid = await refs.lifePersonalRef.current?.submit();
    const planDetailsValid = await refs.lifePlanDetailsRef.current?.submit();
    if (!personalValid || !planDetailsValid) return false;

    const personalData = refs.lifePersonalRef.current!.getValues();
    const planDetails = refs.lifePlanDetailsRef.current!.getValues();

    const responseData = await createLifeSession({
      step: SESSION_STEP_KEYS.PERSONAL_DETAILS,
      ...buildLifeCreateSessionPayload(personalData),
    });
    const ids = getSessionIds(responseData);
    setActiveSessionIds(ids);

    const requestBody = buildLifeQuoteRequestBody(personalData as any, planDetails, ids.session);
    dispatch(setSession(responseData));
    dispatch(setSharedPersonalDetails(personalData as any));
    dispatch(saveLifeQuoteStepFor(LIFE_QUOTE_STEP_KEYS.TERM_DETAILS, planDetails));

    if (ids.clientId && ids.sessionId) {
      await updateLifeSessionStep({
        step: SESSION_STEP_KEYS.PRE_QUOTE_DETAILS,
        payload: {
          personal_details: requestBody.personal_details,
          life_insurance: requestBody.life_insurance,
        },
        clientId: ids.clientId,
        sessionId: ids.sessionId,
        lobId: LOB_IDS.LIFE,
      });
    }

    const collectedQuotes: any[] = [];
    dispatch(clearQuoteList());
    await streamQuotes({
      endpointPath: App_Url_New.ApiEndPoint.lifeGetQuote,
      requestBody,
      onQuoteReceived: (quote) => {
        dispatch(setQuoteList(quote as any));
        dispatch(sortQuotes("sequence-asc"));
        if (quote?.insurer?.name && quote?.plan?.name) collectedQuotes.push(quote);
      },
      onStreamComplete: () => {
        if (!ids.clientId || !ids.sessionId || collectedQuotes.length === 0) return;
        void updateLifeSessionStep({
          step: "quote_request",
          payload: {
            personal_details: requestBody.personal_details,
            life_insurance: requestBody.life_insurance,
            quotes: collectedQuotes,
          },
          clientId: ids.clientId,
          sessionId: ids.sessionId,
          lobId: LOB_IDS.LIFE,
        });
      },
      onStreamError: (err) => console.error("Life quote stream error:", err),
    });
    dispatch(sortQuotes("sequence-asc"));
    return true;
  }, [dispatch, refs, setActiveSessionIds]);
};
